/** Leaflet bounds — continental US + Alaska & Hawaii (pan/zoom clamped to USA). */
export const US_MAP_BOUNDS: [[number, number], [number, number]] = [
  [24.396308, -124.848974],
  [49.384358, -66.885444],
];

export const US_MAP_CENTER: [number, number] = [39.8283, -98.5795];
export const US_MAP_DEFAULT_ZOOM = 5;

const US_STATE_NAME_TO_ABBR: Record<string, string> = {
  ALABAMA: 'AL', ALASKA: 'AK', ARIZONA: 'AZ', ARKANSAS: 'AR', CALIFORNIA: 'CA', COLORADO: 'CO',
  CONNECTICUT: 'CT', DELAWARE: 'DE', FLORIDA: 'FL', GEORGIA: 'GA', HAWAII: 'HI', IDAHO: 'ID',
  ILLINOIS: 'IL', INDIANA: 'IN', IOWA: 'IA', KANSAS: 'KS', KENTUCKY: 'KY', LOUISIANA: 'LA',
  MAINE: 'ME', MARYLAND: 'MD', MASSACHUSETTS: 'MA', MICHIGAN: 'MI', MINNESOTA: 'MN',
  MISSISSIPPI: 'MS', MISSOURI: 'MO', MONTANA: 'MT', NEBRASKA: 'NE', NEVADA: 'NV',
  'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ', 'NEW MEXICO': 'NM', 'NEW YORK': 'NY',
  'NORTH CAROLINA': 'NC', 'NORTH DAKOTA': 'ND', OHIO: 'OH', OKLAHOMA: 'OK', OREGON: 'OR',
  PENNSYLVANIA: 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC', 'SOUTH DAKOTA': 'SD',
  TENNESSEE: 'TN', TEXAS: 'TX', UTAH: 'UT', VERMONT: 'VT', VIRGINIA: 'VA', WASHINGTON: 'WA',
  'WEST VIRGINIA': 'WV', WISCONSIN: 'WI', WYOMING: 'WY', 'DISTRICT OF COLUMBIA': 'DC',
};

export function resolveUsStateAbbr(state?: string): string | null {
  const raw = String(state || '').trim().toUpperCase();
  if (!raw) return null;
  const codes = new Set(Object.values(US_STATE_NAME_TO_ABBR));
  if (raw.length === 2 && codes.has(raw)) return raw;
  return US_STATE_NAME_TO_ABBR[raw] || null;
}

/** Detect when the user is searching for a non-US (usually Canadian) address. */
export function queryImpliesNonUs(query: string): boolean {
  const q = String(query || '').trim();
  if (!q) return false;

  if (/\bcanada\b/i.test(q)) return true;

  if (
    /\b(?:ontario|quebec|québec|british columbia|alberta|manitoba|saskatchewan|nova scotia|new brunswick|newfoundland|prince edward island)\b/i.test(
      q,
    ) &&
    !/\b(?:california|oregon|idaho|new york|pennsylvania)\b/i.test(q)
  ) {
    return true;
  }

  const caProvinceAfterComma =
    /,\s*(on|bc|ab|qc|mb|sk|ns|nb|nl|pe|nt|nu|yt)\s*(?:,|$)/i.test(q);
  if (caProvinceAfterComma && !/\b(united states|usa|us)\b/i.test(q)) return true;

  if (
    /\b(?:toronto|montreal|vancouver|ottawa|calgary|edmonton|winnipeg|quebec city|mississauga|hamilton|halifax)\s*,\s*(on|qc|bc|ab|mb|sk|ns|nb)\b/i.test(
      q,
    )
  ) {
    return true;
  }

  if (
    /\b(?:toronto|montreal|vancouver|ottawa|calgary|edmonton|winnipeg)\s+(on|qc|bc|ab|mb|sk|ns|nb)\b/i.test(
      q,
    )
  ) {
    return true;
  }

  if (
    /\b(on|bc|ab|qc|mb|sk|ns|nb|nl|pe|nt|nu|yt)\s*$/i.test(q) &&
    !/\b(united states|usa|us)\b/i.test(q)
  ) {
    return true;
  }

  return false;
}

export function dedupePlaces<T extends { formatted_address: string }>(places: T[]): T[] {
  const seen = new Set<string>();
  return places.filter((place) => {
    const key = String(place.formatted_address || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
